import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom"; // Import Link for content items
import translations from "../assets/locals/translations"; // Adjust path
import WhatsAppSelection from "./WhatsAppSelection"; // Adjust path
import axios from "axios";

// --- Import CSS ---
import "../styles/ConfigurationSettings.css"; // Import the new/updated CSS

// --- Import Icons (ensure all needed icons are imported) ---
import {
  FaMapMarkedAlt,
  FaMap,
  FaWarehouse,
  FaBuilding,
  FaWhatsapp,
  FaTags,
  FaTasks,
  FaUserTie,
  FaIndustry,
  FaCalendarTimes,
  FaEnvelopeOpenText,
  FaWrench,
  FaUniversity,
  FaCog,
  FaFileInvoiceDollar,
  FaBell,
  FaBoxOpen,
  FaCogs,
  FaStreetView,
  FaCity,
  FaHome,
  FaSitemap,
  FaTools,
  FaFlag,
  FaDollarSign,
  FaAngleRight,
  FaRoute, // Icon for Tracking Section Header
  FaClock, // Icon for time-related settings
  FaToggleOn, // Icon for enabling/disabling
  FaUserShield, // Icon for privacy
  FaClipboardList, // Icon for activity logging
  FaDrawPolygon,
} from "react-icons/fa";
import {
  MdLocationCity,
  MdHolidayVillage,
  MdOutlineSettingsSuggest,
  MdOutlineMiscellaneousServices,
} from "react-icons/md";
import {
  IoDocumentsOutline,
  IoBusinessOutline,
  IoPricetagOutline,
} from "react-icons/io5";
import TrackingEnableModal from "./tool/TrackingEnableModal";
import LocationIntervalModal from "./tool/LocationIntervalModal";

// --- Settings Data Definition (as shown above) ---
// featureFlags: { showAgency?: boolean, showBranch?: boolean }
const settingsConfig = (currentLanguage, translations, featureFlags = {}) => ({
  // ... (paste the settingsConfig object from step 2 here) ...
  basic: {
    id: "basic",
    title: translations[currentLanguage].basicsetup,
    icon: FaMapMarkedAlt,
    items: [
      {
        id: "state",
        title: translations[currentLanguage].state,
        path: "/administration/configuration/state",
        icon: FaFlag,
      },
      {
        id: "district",
        title: translations[currentLanguage].district,
        path: "/administration/configuration/district",
        icon: FaCity,
      },
      {
        id: "taluka",
        title: translations[currentLanguage].taluka,
        path: "/administration/configuration/taluka",
        icon: FaStreetView,
      },
      {
        id: "village",
        title: translations[currentLanguage].village,
        path: "/administration/configuration/village",
        icon: FaHome,
      },
    ],
  },
  agency: {
    id: "agency",
    title: translations[currentLanguage].agencysetting,
    icon: IoBusinessOutline,
    items: [
      ...(featureFlags.showAgency !== false
        ? [
            {
              id: "agency",
              title: translations[currentLanguage].agency,
              path: "/administration/configuration/agency",
              icon: FaUserTie,
            },
          ]
        : []),
      {
        id: "whatsapp",
        title: translations[currentLanguage].whatsapp,
        modal: "whatsapp",
        icon: FaWhatsapp,
      },
      {
        id: "offer",
        title: translations[currentLanguage].offer,
        path: "/administration/configuration/offer",
        icon: IoPricetagOutline,
      },
    ],
  },
  master: {
    id: "master",
    title: translations[currentLanguage].mastersetting,
    icon: FaCogs,
    items: [
      ...(featureFlags.showBranch !== false
        ? [
            {
              id: "branch",
              title: translations[currentLanguage].branch,
              path: "/administration/configuration/branch",
              icon: FaBuilding,
            },
          ]
        : []),
      {
        id: "category",
        title: translations[currentLanguage].category,
        path: "/administration/configuration/category",
        icon: FaSitemap,
      },
      {
        id: "department",
        title: translations[currentLanguage].department,
        path: "/administration/configuration/department",
        icon: FaWarehouse,
      },
      {
        id: "manufacturer",
        title: translations[currentLanguage].manufacturer,
        path: "/administration/configuration/manufacturer",
        icon: FaIndustry,
      },
      {
        id: "task",
        title: translations[currentLanguage].task,
        path: "/administration/configuration/task",
        icon: FaTasks,
      },
      {
        id: "enqsources",
        title: translations[currentLanguage].enqsources,
        path: "/administration/configuration/enquirysources",
        icon: FaEnvelopeOpenText,
      },
      {
        id: "holiday",
        title: translations[currentLanguage].holiday,
        path: "/administration/configuration/holiday",
        icon: FaCalendarTimes,
      },
      {
        id: "message",
        title: translations[currentLanguage].messagelist,
        path: "/administration/configuration/messagelist",
        icon: FaEnvelopeOpenText,
      },
      {
        id: "customfield",
        title: translations[currentLanguage].customfield,
        path: "/administration/configuration/customfield",
        icon: FaWrench,
      },
      {
        id: "bank",
        title: translations[currentLanguage].bank,
        path: "/administration/configuration/addbank",
        icon: FaUniversity,
      },
    ],
  },
  reporting: {
    id: "reporting",
    title: translations[currentLanguage].notficationc,
    icon: FaBell,
    items: [
      {
        id: "notification",
        title: translations[currentLanguage].reportnotification,
        path: "/administration/configuration/notification",
        icon: FaBell,
      },
    ],
  },
  // --- ENHANCED TRACKING SECTION ---
  tracking: {
    id: "tracking",
    title:
      translations[currentLanguage]?.employeeTracking || "EMPLOYEE TRACKING",
    icon: FaRoute, // Changed icon to be more specific to tracking/routes
    items: [
      {
        id: "liveMapView",
        title:
          translations[currentLanguage]?.liveemployeemap || "Live Employee Map",
        // This path should lead to your component that displays the real-time map
        // Adjust the path based on your actual routing structure
        path: "/tracking/live-map", // Example path
        icon: FaMapMarkedAlt, // Using FaMapMarkedAlt for the map view
      },
      {
        id: "trackingEnable",
        title:
          translations[currentLanguage]?.enablelivetrackingmodal || "Enable Live Tracking",
        // This might link to a page with a master toggle and role-based settings
        // path: "/administration/configuration/tracking/status",
        modal: "trackingEnable",
        icon: FaToggleOn,
      },
      {
        id: "trackingInterval",
        title:
          translations[currentLanguage]?.locationupdateinterval || "Location Update Interval",
        // Page to set how often (e.g., 5 mins, 15 mins) location is sent
        modal: "trackingInterval",
        // path: "/administration/configuration/tracking/interval",
        icon: FaClock,
      },
      {
        id: "workingHours",
        title:
          translations[currentLanguage]?.trackingworkinghours || "Tracking Working Hours",
        // Define default hours during which tracking should be active
        path: "/administration/configuration/tracking/working-hours",
        icon: FaCalendarTimes,
      },
      // {
      //   id: "activityLogging",
      //   title: "Activity Logging Rules",
      //   // Configure automatic logging (e.g., check-in/out) or manual options
      //   path: "/administration/configuration/tracking/activity-log",
      //   icon: FaClipboardList,
      // },
      // {
      //   id: "geofencing",
      //   title: "Geofencing Zones",
      //   // Manage virtual boundaries (e.g., client sites, restricted areas)
      //   path: "/administration/configuration/tracking/geofencing",
      //   icon: FaMap, // Or FaDrawPolygon
      // },
      // {
      //   id: "trackingAlerts", // Different ID from reporting alerts
      //   title: "Tracking Specific Alerts",
      //   // Configure alerts based on tracking events (e.g., geofence entry/exit, inactivity)
      //   // NOTE: This needs a DIFFERENT path and component than general notifications
      //   path: "/administration/configuration/tracking/alerts",
      //   icon: FaBell,
      // },
      // {
      //   id: "trackingPrivacy",
      //   title: "Tracking Privacy Settings",
      //   // Settings for user consent, data visibility (who sees location data)
      //   path: "/administration/configuration/tracking/privacy",
      //   icon: FaUserShield,
      // },
    ],
  },
  // --- END OF ENHANCED TRACKING SECTION ---
  parts: {
    id: "parts",
    title: translations[currentLanguage].part,
    icon: FaTools,
    items: [
      {
        id: "parts",
        title: translations[currentLanguage].parts,
        path: "/administration/configuration/partlist",
        icon: FaBoxOpen,
      },
      {
        id: "servicelist",
        title: translations[currentLanguage].servicelist || "Service List",
        path: "/administration/configuration/servicelist",
        icon: MdOutlineMiscellaneousServices,
      },
    ],
  },
  tax: {
    id: "tax",
    title: translations[currentLanguage].tax,
    icon: FaDollarSign,
    items: [
      {
        id: "taxs",
        title: translations[currentLanguage].taxs,
        path: "/administration/configuration/tax",
        icon: FaFileInvoiceDollar,
      },
    ],
  },
});

// --- Main Component ---
// --- Main Component ---
export default function ConfigurationSettings() {
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const currentLanguage = useSelector((state) => state.language.language);
  const [showTrackingEnableModal, setShowTrackingEnableModal] = useState(false);
  const [showLocationIntervalModal, setShowLocationIntervalModal] =
    useState(false);
  const [featureFlags, setFeatureFlags] = useState({});
  const navigate = useNavigate();

  // Fetch configuration flags (config_agency, config_branch) from backend
  useEffect(() => {
    const fetchConfigFlags = async () => {
      try {
        const url = `${process.env.REACT_APP_NODE_URL}/api/agency/get-agencylogo`;
        const response = await axios.get(url);

        if (response?.data?.result && Array.isArray(response.data.result)) {
          const valueObj = response.data.result.reduce((acc, item) => {
            if (item?.key_name && item?.value !== undefined) {
              acc[item.key_name] = item.value;
            }
            return acc;
          }, {});

          const parseFlag = (rawValue, defaultValue = true) => {
            if (rawValue === undefined || rawValue === null) {
              return defaultValue;
            }

            const normalized = String(rawValue).toLowerCase().trim();

            if (["false", "0", "no", "off"].includes(normalized)) {
              return false;
            }
            if (["true", "1", "yes", "on"].includes(normalized)) {
              return true;
            }

            return defaultValue;
          };

          const showAgency = parseFlag(valueObj.config_agency, true);
          const showBranch = parseFlag(valueObj.config_branch, true);

          setFeatureFlags({ showAgency, showBranch });
        }
      } catch (error) {
        console.error("Error fetching configuration flags:", error);
        // In case of error, keep defaults (show all)
      }
    };

    fetchConfigFlags();
  }, []);

  // Memoize the config data based on language
  const config = useMemo(
    () => settingsConfig(currentLanguage, translations, featureFlags),
    [currentLanguage, featureFlags]
  );

  // Get section IDs for iteration
  const sectionIds = useMemo(() => Object.keys(config), [config]);

  // FIX: Initialize state from sessionStorage so it remembers the active tab
  const [selectedSectionId, setSelectedSectionId] = useState(() => {
    const savedSection = sessionStorage.getItem("selectedConfigTab");
    // Check if there's a saved tab and if it still exists in our config
    if (savedSection && Object.keys(config).includes(savedSection)) {
      return savedSection;
    }
    return Object.keys(config)[0] || null;
  });

  // FIX: Save the selected tab to sessionStorage whenever it changes
  useEffect(() => {
    if (selectedSectionId) {
      sessionStorage.setItem("selectedConfigTab", selectedSectionId);
    }
  }, [selectedSectionId]);

  // Ensure selected section stays valid when sections change (e.g., flags updated)
  useEffect(() => {
    if (!selectedSectionId || !config[selectedSectionId]) {
      const firstId = sectionIds[0] || null;
      if (firstId !== selectedSectionId) {
        setSelectedSectionId(firstId);
      }
    }
  }, [sectionIds, config, selectedSectionId]);

  const handleContentItemClick = (item) => {
    if (item.path) {
      navigate(item.path);
    } else if (item.modal === "whatsapp") {
      setShowWhatsAppModal(true);
    } else if (item.modal === "trackingEnable") {
      setShowTrackingEnableModal(true);
    } else if (item.modal === "trackingInterval") {
      setShowLocationIntervalModal(true);
    }
  };

  const selectedSection = config[selectedSectionId];
  // Hide Holiday tab when sidebar_holiday is explicitly set to false/0
  const hideHolidayTab =
    localStorage.getItem("sidebarHoliday") === "false" ||
    localStorage.getItem("sidebarHoliday") === "0";
  const sectionItems = selectedSection
    ? selectedSection.items.filter(
      (item) => !(item.id === "holiday" && hideHolidayTab)
    )
    : [];

  return (
    <div className="config-layout">
      {/* --- Sidebar --- */}
      <aside className="config-sidebar">
        <h2 className="config-sidebar__title">
          {translations[currentLanguage].configuration}
        </h2>
        <nav>
          <ul className="config-sidebar__nav">
            {sectionIds.map((id) => {
              const section = config[id];
              const Icon = section.icon; // Get the icon component
              const isActive = selectedSectionId === id;
              return (
                <li key={id} className="config-sidebar__item">
                  <button
                    type="button"
                    className={`config-sidebar__button ${isActive ? "config-sidebar__button--active" : ""
                      }`}
                    onClick={() => setSelectedSectionId(id)}
                  >
                    {Icon && <Icon />} {/* Render icon if provided */}
                    <span>{section.title}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>

      {/* --- Content Area --- */}
      <main className="config-content-area">
        {selectedSection ? (
          <>
            <h1 className="config-content-area__title">
              {selectedSection.title}
            </h1>
            <ul className="config-content__list">
              {sectionItems.map((item) => {
                const ItemIcon = item.icon; // Get the item's icon component
                // Render a Link for navigation items, Button for modal triggers
                if (item.path) {
                  return (
                    <li key={item.id} className="config-content__list-item">
                      <Link to={item.path} className="config-content__link">
                        {ItemIcon && <ItemIcon />}
                        <span>{item.title}</span>
                      </Link>
                    </li>
                  );
                } else if (item.modal) {
                  return (
                    <li key={item.id} className="config-content__list-item">
                      <button
                        type="button"
                        onClick={() => handleContentItemClick(item)}
                        className="config-content__link"
                        style={{ width: "100%" }}
                      >
                        {ItemIcon && <ItemIcon />}
                        <span>{item.title}</span>
                      </button>
                    </li>
                  );
                }
                return null;
              })}
            </ul>
          </>
        ) : (
          <div>Select a section from the sidebar.</div>
        )}
      </main>

      {/* Modals */}
      {showWhatsAppModal && (
        <WhatsAppSelection
          showModal={showWhatsAppModal}
          handleCloseModal={() => setShowWhatsAppModal(false)}
        />
      )}
      {showTrackingEnableModal && (
        <TrackingEnableModal
          showModal={showTrackingEnableModal}
          handleCloseModal={() => setShowTrackingEnableModal(false)}
          setShowTrackingEnableModal={setShowTrackingEnableModal}
        />
      )}

      {showLocationIntervalModal && (
        <LocationIntervalModal
          showModal={showLocationIntervalModal}
          handleCloseModal={() => setShowLocationIntervalModal(false)}
          setShowLocationIntervalModal={setShowLocationIntervalModal}
        />
      )}
    </div>
  );
}